import React, { useEffect } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import Router from 'next/router';

// Constants.
import { PRODUCT_FILTER_TYPE, CREDIT_CARD_FILTER } from 'app/constants/product';

// Components.
import Page from 'app/components/misc/BasePage';
import PageHead from 'app/components/misc/PageHead';
import ReviewsContent from 'app/pageContents/ReviewsContent';

// Utils.
import { configureReviewPageHead } from 'app/utils/configurePageHead';

import {
  getProducts,
  getProductCategories,
  getPrismic,
} from 'app/dispatcher/productDispatcher';

import { getReviewPageProps } from 'app/selectors/productSelector';
import dispatchWithPromise from 'app/utils/withPromise';
import slugify from 'slugify';

const overrideWrapperStyles = {
  justifyContent: 'stretch',
  flexDirection: 'column',
};

const Reviews = ({ categorySlug: urlSlug, bankSlug }) => {
  // Selectors
  const reviewHeaderProps = useSelector(getReviewPageProps, shallowEqual);
  const actualSlug = useSelector(
    state => state.product.meta.categorySlug,
    shallowEqual,
  );

  // Effects
  useEffect(() => {
    if (urlSlug && urlSlug !== actualSlug) {
      const reviewAs = `/reviews?categorySlug=${actualSlug}`;
      const reviewLink = `/reviews/${actualSlug}`;
      Router.push(reviewAs, reviewLink, { shallow: true });
    }
  }, []);

  return (
    <Page pageContentStyle={overrideWrapperStyles}>
      <PageHead
        {...configureReviewPageHead(reviewHeaderProps.seoMeta, bankSlug)}
        verticalCode={urlSlug === 'car-insurance' ? 'CI' : urlSlug}
        pageType="result page"
      />
      <ReviewsContent />
    </Page>
  );
};

Reviews.getInitialProps = async ({ store, query }) => {
  const { dispatch } = store;
  // Show the most reviewed when in /reviews page,
  // show only most popular (custom ranking) for individual category pages.
  const { categorySlug, cardTypes, bankProviders } = query;

  query.sortType = categorySlug
    ? PRODUCT_FILTER_TYPE.MOST_POPULAR
    : PRODUCT_FILTER_TYPE.MOST_REVIEWED;
  query.productFilter = {};

  let bankSlug;

  if (cardTypes) {
    query.productFilter[CREDIT_CARD_FILTER.CARD_TYPES] = [cardTypes];
  }
  if (bankProviders) {
    query.productFilter[CREDIT_CARD_FILTER.BANK_PROVIDERS] = [bankProviders];
    bankSlug = slugify(bankProviders, { lower: true });
  }

  if (categorySlug) {
    let prismicSlug = categorySlug;

    if (categorySlug === 'credit-cards' && bankProviders) {
      prismicSlug = `${categorySlug}-${bankSlug}`;
    }

    dispatch(getPrismic(prismicSlug));
  }

  await Promise.all([
    dispatchWithPromise(dispatch, getProductCategories),
    dispatchWithPromise(dispatch, getProducts, query),
  ]);

  return { categorySlug, bankProviders, bankSlug };
};

export default Reviews;
